import React, { useState } from "react";
import {
  Button,
  Space,
  Typography,
  Tag,
  Tooltip,
  Segmented,
  Modal,
  Input,
  DatePicker,
  Alert,
  Table,
} from "antd";
import {
  DesktopOutlined,
  EyeOutlined,
  HeartOutlined,
  InboxOutlined,
  UndoOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import PageHeader from "./common/PageHeader";
import InfoHint from "./common/InfoHint";
import DataTable from "./common/DataTable";
import { listCameras, archiveDevices, unarchiveDevices } from "../network/api";
import { relativeTime } from "../constants/health";
import {
  VERSION_STATUS,
  VERSION_STATUS_TAG_COLOR,
  VERSION_STATUS_LABEL,
  versionLabel,
  versionTooltip,
} from "../constants/agentVersion";
import { useAuth } from "../context/AuthContext";
import { notify, serverError } from "../utils/notify";

const { Text, Paragraph } = Typography;

const SCOPE = { ACTIVE: "false", ARCHIVED: "true", ALL: "all" };

// The prod golden image was rolled on this date — everything registered before it
// belongs to the superseded test run. Pre-fills the bulk-archive cutoff.
const GOLDEN_IMAGE_DATE = "2026-07-01";

const Devices = () => {
  const navigate = useNavigate();
  const { isHardwareAdmin } = useAuth();

  const [scope, setScope] = useState(SCOPE.ACTIVE);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [busy, setBusy] = useState(false);

  // Bulk "archive an entire registration era" modal.
  const [sweepOpen, setSweepOpen] = useState(false);
  const [cutoff, setCutoff] = useState(dayjs(GOLDEN_IMAGE_DATE));
  const [sweepReason, setSweepReason] = useState("Superseded test run (pre golden image)");
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  const viewingArchive = scope === SCOPE.ARCHIVED;
  const reload = () => {
    setSelectedKeys([]);
    setRefreshKey((k) => k + 1);
  };

  const runArchive = async (payload, successMsg) => {
    setBusy(true);
    try {
      const res = await archiveDevices(payload);
      notify.success(`${successMsg} (${res.archived} device${res.archived === 1 ? "" : "s"})`);
      reload();
      return res;
    } catch (e) {
      notify.error(serverError(e, "Failed to archive"));
    } finally {
      setBusy(false);
    }
  };

  const runRestore = async (ids) => {
    setBusy(true);
    try {
      const res = await unarchiveDevices({ ids });
      notify.success(`Restored ${res.restored} device${res.restored === 1 ? "" : "s"}`);
      reload();
    } catch (e) {
      notify.error(serverError(e, "Failed to restore"));
    } finally {
      setBusy(false);
    }
  };

  const confirmArchive = (record) =>
    Modal.confirm({
      title: `Archive ${record.deviceTitle || record.deviceId}?`,
      icon: <ExclamationCircleOutlined />,
      content:
        "The device leaves the live fleet — no health monitoring, no preventive visits and no agent rollouts. Its history is kept and you can restore it at any time.",
      okText: "Archive",
      okButtonProps: { danger: true },
      onOk: () => runArchive({ ids: [record._id] }, "Archived"),
    });

  const confirmBulkArchive = () =>
    Modal.confirm({
      title: `Archive ${selectedKeys.length} selected device${selectedKeys.length === 1 ? "" : "s"}?`,
      icon: <ExclamationCircleOutlined />,
      content: "They leave the live fleet but keep their history, and can be restored later.",
      okText: "Archive",
      okButtonProps: { danger: true },
      onOk: () => runArchive({ ids: selectedKeys }, "Archived"),
    });

  // Dry run first: the operator sees exactly which devices the cutoff catches
  // before anything is written.
  const openSweep = async () => {
    setSweepOpen(true);
    setPreview(null);
    await loadPreview(cutoff);
  };

  const loadPreview = async (date) => {
    if (!date) return;
    setPreviewing(true);
    try {
      setPreview(
        await archiveDevices({ registeredBefore: date.startOf("day").toISOString(), dryRun: true })
      );
    } catch (e) {
      notify.error(serverError(e, "Failed to preview"));
      setPreview(null);
    } finally {
      setPreviewing(false);
    }
  };

  const submitSweep = async () => {
    if (!preview?.matched) return;
    const res = await runArchive(
      {
        registeredBefore: cutoff.startOf("day").toISOString(),
        reason: sweepReason || undefined,
      },
      "Archived the previous test run"
    );
    if (res) setSweepOpen(false);
  };

  const columns = [
    {
      title: "Device ID",
      dataIndex: "deviceId",
      key: "deviceId",
      sorter: true,
      render: (id, record) => (
        <Space size={6}>
          <Text strong>{id}</Text>
          {record.archivedAt && <Tag>Archived</Tag>}
        </Space>
      ),
    },
    {
      title: "Title",
      dataIndex: "deviceTitle",
      key: "deviceTitle",
      sorter: true,
      render: (title) => title || <Text type="secondary">—</Text>,
    },
    {
      title: <span>Agent version<InfoHint title="The edge-agent software running on the device, and whether it is up to date. Hover a row's tag for details; “→ x.y.z” means an update to that version is pending." /></span>,
      key: "agentVersion",
      render: (_, record) => {
        const av = record.agentVersion;
        const status = av?.status || VERSION_STATUS.UNKNOWN;
        const unknown = status === VERSION_STATUS.UNKNOWN;
        return (
          <Tooltip title={versionTooltip(av)}>
            <Space direction="vertical" size={0}>
              <Tag color={VERSION_STATUS_TAG_COLOR[status]} style={{ marginInlineEnd: 0 }}>
                {versionLabel(av)}
              </Tag>
              <Text type="secondary" style={{ fontSize: 11 }}>
                {unknown
                  ? VERSION_STATUS_LABEL[status]
                  : status === VERSION_STATUS.OUTDATED && av?.target?.version
                  ? `→ ${av.target.version}`
                  : VERSION_STATUS_LABEL[status]}
              </Text>
            </Space>
          </Tooltip>
        );
      },
    },
    {
      title: <span>Network / IP<InfoHint title="The network the device last reported connecting through, and its current IP address." /></span>,
      key: "network",
      render: (_, record) =>
        record.currentNetwork || record.currentIp ? (
          <Text>
            {record.currentNetwork || "—"}
            {record.currentIp ? (
              <Text type="secondary"> ({record.currentIp})</Text>
            ) : null}
          </Text>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: <span>Config<InfoHint title="The capture/behaviour configuration profile assigned to this device. “Unassigned” means it is running defaults." /></span>,
      dataIndex: ["config", "title"],
      key: "config",
      render: (_, record) =>
        record.config?.title || <Text type="secondary">Unassigned</Text>,
    },
    {
      title: <span>Outlet<InfoHint title="The shop/outlet this device is installed in. “—” means it hasn't been linked to an outlet yet." /></span>,
      dataIndex: ["shop", "title"],
      key: "shop",
      render: (_, record) =>
        record.shop?.title ? (
          <Tag color="purple">{record.shop.title}</Tag>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: viewingArchive ? "Archived" : "Updated",
      dataIndex: viewingArchive ? "archivedAt" : "updatedAt",
      key: viewingArchive ? "archivedAt" : "updatedAt",
      sorter: true,
      render: (value) => relativeTime(value),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space size="small" onClick={(e) => e.stopPropagation()}>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() =>
              navigate(`/dashboard/device-details/${record._id}`)
            }
          >
            View details
          </Button>
          <Button
            type="link"
            size="small"
            icon={<HeartOutlined />}
            onClick={() =>
              navigate(`/dashboard/health/${record.deviceId}`)
            }
          >
            Health
          </Button>
          {isHardwareAdmin &&
            (record.archivedAt ? (
              <Button
                type="link"
                size="small"
                icon={<UndoOutlined />}
                loading={busy}
                onClick={() => runRestore([record._id])}
              >
                Restore
              </Button>
            ) : (
              <Button
                type="link"
                size="small"
                danger
                icon={<InboxOutlined />}
                onClick={() => confirmArchive(record)}
              >
                Archive
              </Button>
            ))}
        </Space>
      ),
    },
  ];

  const previewColumns = [
    { title: "Device ID", dataIndex: "deviceId", key: "deviceId" },
    { title: "Title", dataIndex: "deviceTitle", key: "deviceTitle", render: (t) => t || "—" },
    {
      title: "Registered",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (v) => (v ? dayjs(v).format("YYYY-MM-DD HH:mm") : "—"),
    },
    {
      title: "Last seen",
      dataIndex: "lastSeenAt",
      key: "lastSeenAt",
      render: (v) => (v ? relativeTime(v) : "never"),
    },
  ];

  const toolbar = (
    <Space wrap>
      <Segmented
        value={scope}
        onChange={(v) => {
          setScope(v);
          setSelectedKeys([]);
        }}
        options={[
          { label: "Active", value: SCOPE.ACTIVE },
          { label: "Archived", value: SCOPE.ARCHIVED },
          { label: "All", value: SCOPE.ALL },
        ]}
      />
      {isHardwareAdmin && selectedKeys.length > 0 && !viewingArchive && (
        <Button danger icon={<InboxOutlined />} loading={busy} onClick={confirmBulkArchive}>
          Archive selected ({selectedKeys.length})
        </Button>
      )}
      {isHardwareAdmin && selectedKeys.length > 0 && viewingArchive && (
        <Button icon={<UndoOutlined />} loading={busy} onClick={() => runRestore(selectedKeys)}>
          Restore selected ({selectedKeys.length})
        </Button>
      )}
    </Space>
  );

  return (
    <div>
      <PageHeader
        title="Devices"
        subtitle="Camera fleet inventory, agent versions & assigned configurations"
        tooltip="The full camera inventory. Switch between Active, Archived and All with the toggle. Archiving removes a device from the live fleet (no monitoring or rollouts) but keeps its history so it can be restored."
        icon={<DesktopOutlined style={{ fontSize: 22, color: "#2f54eb" }} />}
        extra={
          isHardwareAdmin ? (
            <Button icon={<InboxOutlined />} onClick={openSweep}>
              Archive by registration date…
            </Button>
          ) : null
        }
      />

      <DataTable
        fetcher={listCameras}
        filters={{ archived: scope }}
        refreshKey={refreshKey}
        rowKey="_id"
        columns={columns}
        toolbar={toolbar}
        searchPlaceholder="Search devices…"
        scrollX={1200}
        emptyText={viewingArchive ? "No archived devices" : "No devices found"}
        rowSelection={
          isHardwareAdmin
            ? {
                selectedRowKeys: selectedKeys,
                onChange: setSelectedKeys,
                preserveSelectedRowKeys: true,
              }
            : undefined
        }
        onRow={(record) => ({
          style: { cursor: "pointer", opacity: record.archivedAt ? 0.6 : 1 },
          onClick: () =>
            navigate(`/dashboard/device-details/${record._id}`),
        })}
      />

      <Modal
        title="Archive devices by registration date"
        open={sweepOpen}
        onCancel={() => setSweepOpen(false)}
        onOk={submitSweep}
        okText={preview?.matched ? `Archive ${preview.matched} device${preview.matched === 1 ? "" : "s"}` : "Archive"}
        okButtonProps={{ danger: true, disabled: !preview?.matched, loading: busy }}
        width={760}
      >
        <Paragraph type="secondary">
          Archives every device that registered before the chosen date. Use it to retire a
          superseded test run — the prod golden image was rolled on {GOLDEN_IMAGE_DATE}, so
          anything registered before then belongs to the previous batch.
        </Paragraph>

        <Space wrap style={{ marginBottom: 12 }}>
          <DatePicker
            value={cutoff}
            allowClear={false}
            onChange={(d) => {
              setCutoff(d);
              loadPreview(d);
            }}
          />
          <Input
            style={{ width: 340 }}
            placeholder="Reason (recorded on each device)"
            value={sweepReason}
            onChange={(e) => setSweepReason(e.target.value)}
          />
        </Space>

        {preview && !preview.matched && (
          <Alert type="info" showIcon message="No active devices registered before this date." />
        )}

        {preview?.matched > 0 && (
          <>
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message={`${preview.matched} device${preview.matched === 1 ? "" : "s"} will be archived`}
              description="Their history is kept and they can be restored individually from the Archived tab."
            />
            <Table
              size="small"
              rowKey="_id"
              columns={previewColumns}
              dataSource={preview.devices}
              loading={previewing}
              pagination={{ pageSize: 8, showSizeChanger: false }}
              scroll={{ x: 620 }}
            />
          </>
        )}
      </Modal>
    </div>
  );
};

export default Devices;
